import {
  ALL_SHELLS,
  AUTO_SHELL_COPY,
  layoutShellForVibe,
  SHELL_COPY,
  SHELL_META,
  type LayoutShell,
} from '@flashpoint/site-dna';
import type { StepProps } from '../stepProps';
import { OptionCard } from '../OptionCard';

/**
 * LayoutStep - pick a Nuzum Method layout shell, or leave on Auto.
 *
 * All cards consume structured WizardOptionCopy from @flashpoint/site-dna/copy
 * so the same strings power the in-wizard cards AND the printable client
 * leave-behind on the Review step.
 */
export function LayoutStep({ lead, queueField }: StepProps) {
  const vibe = lead.siteDna.designTokens.vibe;
  const explicit = lead.siteDna.designTokens.layoutShell;
  const autoShell = layoutShellForVibe(vibe);
  const isAuto = !explicit;

  const selectAuto = () => queueField('siteDna.designTokens.layoutShell', undefined);
  const selectShell = (shell: LayoutShell) =>
    queueField('siteDna.designTokens.layoutShell', shell);

  // Customize the Auto card's pitch to mention the resolved shell for THIS vibe.
  const autoCopy = {
    ...AUTO_SHELL_COPY,
    name: `Auto (${SHELL_META[autoShell].number} ${SHELL_META[autoShell].name})`,
    oneLiner: `Auto-resolves to ${SHELL_META[autoShell].name} for the ${vibe} vibe.`,
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-nm-muted">
        Layout shells decide the structure of the site. Vibes color it; shells compose it. Pick
        Auto and we resolve a sensible shell from the {vibe} vibe at build time, or pick a
        specific shell to override. You can change this later without losing other wizard work.
      </p>

      <OptionCard
        copy={autoCopy}
        selected={isAuto}
        onSelect={selectAuto}
        badge="Auto"
        meta={
          <span className="rounded bg-flashpoint-ember/20 px-1.5 py-0.5 text-[10px] text-flashpoint-ember">
            Recommended
          </span>
        }
        ariaId="layout-auto"
      />

      <div className="text-[11px] uppercase tracking-widest text-nm-muted">
        Or pick a specific shell
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {ALL_SHELLS.map((id) => {
          const meta = SHELL_META[id];
          const copy = SHELL_COPY[id];
          const selected = explicit === id;
          const isAutoChoice = id === autoShell;

          return (
            <OptionCard
              key={id}
              copy={copy}
              selected={selected}
              onSelect={() => selectShell(id)}
              badge={meta.number}
              ariaId={`layout-${id}`}
              meta={
                <div className="flex flex-col items-end gap-0.5">
                  <span>density · {meta.density}</span>
                  <span>drift · {meta.drift}</span>
                  {isAutoChoice && (
                    <span className="rounded bg-flashpoint-ember/20 px-1.5 py-0.5 text-[10px] text-flashpoint-ember">
                      Auto pick
                    </span>
                  )}
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}
